/**
 * Catálogo único de módulos exibidos em /select-module.
 *
 * Lista completa (incluindo "coming_soon") porque a tela de seleção mostra
 * tudo. Não confundir com `MODULES` (registry de páginas em
 * `src/lib/modules/registry.ts`), que contém apenas os 9 clones implementados.
 */
import {
  Briefcase, GraduationCap, Scale, PlayCircle, BookOpen, Gavel,
  Stethoscope, Shield, FileStack, Landmark, Languages, ShieldCheck,
  School, Building, Siren, type LucideIcon,
} from 'lucide-react';
import type { ModuleType } from '@/hooks/useModuleAccess';

export type ModuleStatus = 'available' | 'free' | 'coming_soon';

export interface ModuleCard {
  key: ModuleType;
  title: string;
  description: string;
  icon: LucideIcon;
  bullets: string[];
  /** Tailwind palette name (mapeada em colorPalettes.ts). */
  color: string;
  route: string;
  status: ModuleStatus;
}

export const SELECTABLE_MODULES: ModuleCard[] = [
  {
    key: 'concursos',
    title: 'Concursos Públicos',
    description: 'Prepare-se para editais de todo o Brasil',
    icon: Briefcase,
    bullets: ['Questões de todas as bancas', 'Simulados personalizados', 'Estatísticas por disciplina'],
    color: 'emerald',
    route: '/dashboard',
    status: 'available',
  },
  {
    key: 'enem',
    title: 'ENEM',
    description: 'Domine o maior vestibular do Brasil',
    icon: GraduationCap,
    bullets: ['Questões por competência', 'Simulado completo 5h30', 'Análise TRI detalhada'],
    color: 'blue',
    route: '/enem',
    status: 'available',
  },
  {
    key: 'oab',
    title: 'OAB',
    description: 'Conquiste sua carteira da Ordem',
    icon: Scale,
    bullets: ['Questões de exames anteriores', 'Simulados no formato oficial', 'Disciplinas de Direito'],
    color: 'amber',
    route: '/oab',
    status: 'available',
  },
  {
    key: 'videoaulas',
    title: 'Videoaulas',
    description: 'Assista aulas dos melhores professores',
    icon: PlayCircle,
    bullets: ['Aulas em vídeo de qualidade', 'Material complementar', 'Acompanhe seu progresso'],
    color: 'purple',
    route: '/videoaulas',
    status: 'free',
  },
  {
    key: 'vestibulares',
    title: 'Vestibulares Tradicionais',
    description: 'FUVEST, UNICAMP, UNESP, UERJ, UFRGS, ITA, IME',
    icon: BookOpen,
    bullets: ['Provas de 1ª e 2ª fase', 'Questões discursivas', 'Bancos por universidade'],
    color: 'indigo',
    route: '/vestibulares',
    status: 'available',
  },
  {
    key: 'carreiras-juridicas',
    title: 'Carreiras Jurídicas',
    description: 'Magistratura, MP, Defensoria, Procuradorias, AGU',
    icon: Gavel,
    bullets: ['Magistratura e MP Federal/Estadual', 'Defensorias e Procuradorias', 'Bancas Cespe/FCC/FGV/Vunesp'],
    color: 'rose',
    route: '/carreiras-juridicas',
    status: 'available',
  },
  {
    key: 'residencia',
    title: 'Residência Médica',
    description: 'USP, UNIFESP, ENARE, Revalida',
    icon: Stethoscope,
    bullets: ['Casos clínicos comentados', 'Filtro por especialidade', 'Simulados cronometrados'],
    color: 'teal',
    route: '/residencia',
    status: 'available',
  },
  {
    key: 'militares',
    title: 'Concursos Militares',
    description: 'ESA, EsPCEx, AFA, EFOMM, EEAR, IME, ITA',
    icon: Shield,
    bullets: ['Provas das 3 forças armadas', 'Vestibulares militares e carreira', 'AFA, EFOMM, EsPCEx, IME, ITA e mais'],
    color: 'olive',
    route: '/militares',
    status: 'available',
  },
  {
    key: 'cnu',
    title: 'CNU',
    description: 'Concurso Nacional Unificado — todos os blocos',
    icon: FileStack,
    bullets: ['Provas dos 8 blocos temáticos', 'FGV, Cesgranrio e órgãos federais', 'Filtro por bloco e órgão'],
    color: 'green',
    route: '/cnu',
    status: 'available',
  },
  {
    key: 'bancario',
    title: 'Bancário',
    description: 'Banco do Brasil, Caixa, BNDES, BACEN, BNB',
    icon: Landmark,
    bullets: ['BB, Caixa, BNDES, BACEN, BNB, BASA', 'Bancos estaduais (BRB, Banrisul, Banestes)', 'Bancas Cesgranrio, FCC, FGV e Cespe'],
    color: 'yellow',
    route: '/bancario',
    status: 'available',
  },
  {
    key: 'idiomas',
    title: 'Idiomas / Proficiência',
    description: 'TOEFL, IELTS, CELPE-Bras, DELE',
    icon: Languages,
    bullets: ['Listening cronometrado', 'Reading & writing', 'Simulados oficiais'],
    color: 'orange',
    route: '/idiomas',
    status: 'coming_soon',
  },
  {
    key: 'colegios-militares',
    title: 'Colégios Militares & Técnicos',
    description: 'Colégio Pedro II, CEFETs, IFs, Colégio Naval, EPCAR',
    icon: ShieldCheck,
    bullets: ['Provas de admissão e ingresso', 'Conteúdo de fundamental e médio', '5 mil+ questões reais'],
    color: 'slate',
    route: '/colegios',
    status: 'available',
  },
  {
    key: 'reforco-escolar',
    title: 'Reforço Escolar / Ensino Médio',
    description: 'ENEM + provas escolares',
    icon: School,
    bullets: ['Questões do ENEM e escolares', 'Conteúdo do Ensino Fundamental e Médio', 'Simulados por matéria e ano'],
    color: 'violet',
    route: '/reforco-escolar',
    status: 'coming_soon',
  },
  {
    key: 'tribunais',
    title: 'Tribunais e Justiça',
    description: 'TRT, TST, TRE, TRF, TJ, STJ, STF',
    icon: Building,
    bullets: ['Técnico e Analista Judiciário', 'Bancas Cespe/FCC/FGV', 'Provas objetivas e discursivas'],
    color: 'red',
    route: '/tribunais',
    status: 'available',
  },
  {
    key: 'carreiras-policiais',
    title: 'Carreiras Policiais',
    description: 'PF, PRF, PC, PM, Polícia Penal, Depen',
    icon: Siren,
    bullets: ['Agente, Escrivão e Delegado', 'PF/PRF/PM/PC e Polícia Penal', 'Bancas Cespe/FGV/IBFC'],
    color: 'blue',
    route: '/carreiras-policiais',
    status: 'available',
  },
];

export const SELECT_CATEGORIES: { title: string; subtitle: string; keys: ModuleType[] }[] = [
  {
    title: 'Concursos & Carreiras Públicas',
    subtitle: 'Prepare-se para editais e cargos públicos em todo o Brasil',
    keys: ['concursos', 'tribunais', 'carreiras-juridicas', 'cnu', 'carreiras-policiais', 'militares', 'bancario'],
  },
  {
    title: 'Exames & Vestibulares',
    subtitle: 'Provas oficiais, vestibulares e certificações',
    keys: ['oab', 'enem', 'vestibulares', 'residencia', 'colegios-militares', 'reforco-escolar', 'idiomas'],
  },
  {
    title: 'Conteúdo & Aprendizado',
    subtitle: 'Aulas e materiais complementares para todos os módulos',
    keys: ['videoaulas'],
  },
];

export const HERO_TAGLINES: Record<string, string> = {
  concursos: 'Edital aberto, futuro decidido. Treine com questões de todas as bancas do Brasil.',
  tribunais: 'Da audiência ao acórdão. Domine TRT, TJ, STJ e os principais tribunais.',
  'carreiras-juridicas': 'Da toga ao concurso. Magistratura, MP, Defensoria e Procuradorias.',
  'carreiras-policiais': 'Da farda ao distintivo. PF, PRF, PM e PC com questões CESPE e bancas oficiais.',
  cnu: 'Um concurso, oito blocos, um futuro. FGV, Cesgranrio e órgãos federais em um só lugar.',
  bancario: 'Do caixa à carreira de bancário. BB, Caixa, BNDES, BACEN e BNB em questões oficiais.',
  militares: 'Da farda à formação. AFA, EFOMM, EsPCEx, IME, ITA, ESA e as principais escolas militares.',
  oab: 'Sua carteira da Ordem mais perto. Exames anteriores, simulados e doutrina.',
  enem: 'O vestibular que muda vidas. Simulado completo, TRI e análise por competência.',
  vestibulares: 'Da redação ao gabarito. FUVEST, UNICAMP, UNESP, UERJ, ITA, IME e os principais vestibulares tradicionais.',
  residencia: 'Da prova à residência dos sonhos. USP, UNIFESP, ENARE e Revalida com casos clínicos comentados.',
  videoaulas: 'Aprenda assistindo. Aulas dos melhores professores, materiais e progresso.',
  'colegios-militares': 'Tradição, disciplina e excelência. Colégio Pedro II, CEFETs, IFs, Colégio Naval e EPCAR com 5 mil+ questões reais.',
};

export const HERO_KEYS: ModuleType[] = [
  'concursos', 'tribunais', 'carreiras-juridicas', 'carreiras-policiais',
  'cnu', 'bancario', 'militares', 'colegios-militares',
  'oab', 'enem', 'vestibulares', 'residencia', 'videoaulas',
];

export const STATUS_ORDER: Record<ModuleStatus, number> = { available: 0, free: 1, coming_soon: 2 };
